import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentLang = i18n.language;

  const changeLang = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  const catName = (name: string, namePt: string, nameDe: string) =>
    currentLang === 'pt' ? namePt : currentLang === 'de' ? nameDe : name;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 hover:text-primary-700 transition-colors whitespace-nowrap">
            <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-white rounded-md font-body text-sm font-bold flex-shrink-0">
              CS
            </span>
            CSSBuy Spreadsheet
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            <a href="/" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_home')}
            </a>
            <a href="/article/cssbuy-guide" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_shopping_guide')}
            </a>
            <a href="/guides" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_guides')}
            </a>
            <a href="/article/cssbuy-review-2026" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_reviews')}
            </a>
            <a href="/article/cssbuy-shipping-cost" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_shipping')}
            </a>
            <a href="/article/cssbuy-coupon" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_coupons')}
            </a>
            <a href="/article/cssbuy-safety-guide" className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap">
              {t('nav_safety')}
            </a>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap flex items-center gap-1"
              >
                {t('category_dropdown_label')}
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="py-2 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <img src={cat.imageUrl} alt={cat.name} className="w-9 h-9 rounded object-cover flex-shrink-0" />
                        <span className="text-sm text-foreground-800">{catName(cat.name, cat.namePt, cat.nameDe)}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Language Switcher */}
            <div className="relative ml-1">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="px-3 py-2 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap flex items-center gap-1"
              >
                {currentLang === 'pt' ? 'PT' : currentLang === 'de' ? 'DE' : 'EN'}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden z-50">
                  <button onClick={() => changeLang('pt')} className={`block px-4 py-2 text-sm w-full text-left hover:bg-background-100 ${currentLang === 'pt' ? 'text-primary-600 font-semibold' : 'text-foreground-700'}`}>
                    {t('lang_pt')}
                  </button>
                  <button onClick={() => changeLang('en')} className={`block px-4 py-2 text-sm w-full text-left hover:bg-background-100 ${currentLang === 'en' ? 'text-primary-600 font-semibold' : 'text-foreground-700'}`}>
                    {t('lang_en')}
                  </button>
                  <button onClick={() => changeLang('de')} className={`block px-4 py-2 text-sm w-full text-left hover:bg-background-100 ${currentLang === 'de' ? 'text-primary-600 font-semibold' : 'text-foreground-700'}`}>
                    {t('lang_de')}
                  </button>
                </div>
              )}
            </div>

            {/* Main CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('main_cta_button')}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-foreground-900 hover:text-primary-600 flex items-center justify-center"
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a href="/" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_home')}</a>
            <a href="/article/cssbuy-guide" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_shopping_guide')}</a>
            <a href="/guides" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_guides')}</a>
            <a href="/article/cssbuy-review-2026" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_reviews')}</a>
            <a href="/article/cssbuy-shipping-cost" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_shipping')}</a>
            <a href="/article/cssbuy-coupon" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_coupons')}</a>
            <a href="/article/cssbuy-safety-guide" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 hover:text-primary-600 rounded-lg hover:bg-background-100" onClick={() => setMobileOpen(false)}>{t('nav_safety')}</a>

            {/* Mobile Categories Grid */}
            <div className="pt-3 mt-1 border-t border-background-200">
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                {t('category_dropdown_label')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-background-100 transition-colors"
                  >
                    <img src={cat.imageUrl} alt={cat.name} className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                    <span className="text-xs font-medium text-foreground-800 leading-tight">
                      {catName(cat.name, cat.namePt, cat.nameDe)}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Mobile CTA + Language */}
            <div className="pt-3 mt-1 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors"
              >
                {t('main_cta_button')}
                <i className="ri-external-link-line" />
              </a>
            </div>
            <div className="pt-2 flex items-center gap-2 px-3">
              <button onClick={() => changeLang('pt')} className={`px-3 py-1.5 text-xs font-medium rounded-md ${currentLang === 'pt' ? 'bg-primary-100 text-primary-700' : 'text-foreground-500 bg-background-100'}`}>{t('lang_pt')}</button>
              <button onClick={() => changeLang('en')} className={`px-3 py-1.5 text-xs font-medium rounded-md ${currentLang === 'en' ? 'bg-primary-100 text-primary-700' : 'text-foreground-500 bg-background-100'}`}>{t('lang_en')}</button>
              <button onClick={() => changeLang('de')} className={`px-3 py-1.5 text-xs font-medium rounded-md ${currentLang === 'de' ? 'bg-primary-100 text-primary-700' : 'text-foreground-500 bg-background-100'}`}>{t('lang_de')}</button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}